"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import { useAgent } from "../../../../../utils/agent/AgentProvider.js";
import { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { TextField, Button } from "@nxt-costco-com/forge-components";
import { SpaceLg, SpaceSm } from "@nxt-costco-com/forge-design-tokens";
import { focusIntoRef } from "../lib.js";
import { addToListActions } from "../../constants.js";
import { useAuth } from "../../../../../hooks/auth/useAuth/index.js";
import { ShowNotification } from "./Notifications/ShowNotification.js";
import { ProductUI } from "./ProductUI.js";
const CreateNewList = (props) => {
  const notificationRef = useRef(null);
  const { agent } = useAgent();
  const { getToken } = useAuth();
  const {
    dispatch,
    enableModernApis,
    locale,
    msStore,
    product,
    siteValue,
    state
  } = props;
  const { clientIdLegacy, drawerContent, errorMsg, successMsg, wishLists } = state;
  const textFieldInputRef = useRef();
  const [listName, setListName] = useState("");
  const [isError, setError] = useState(false);
  const [listInputError, setListInputError] = useState("");
  const saveToListTitle = msStore.getKeyValueItem("savetolisttitle");
  const createNewListTitle = msStore.getKeyValueItem("createnewlisttitle");
  const createNewListButtonTitle = msStore.getKeyValueItem(
    "createnewlistbuttontitle"
  );
  const cancelButtonLabel = msStore.getKeyValueItem("cancelbuttonlabel");
  const listNameFieldLabel = msStore.getKeyValueItem("listnamefieldlabel");
  const duplicateWishListError = msStore.getKeyValueItem(
    "duplicatewishlisterror"
  );
  const createWishListError = msStore.getKeyValueItem("createwishlisterror");
  const createWishListSuccess = msStore.getKeyValueItem(
    "createwishlistsuccess"
  );
  const requiredListNameError = msStore.getKeyValueItem(
    "listnamerequirederrormessage"
  );
  const validListNameError = msStore.getKeyValueItem("entervalidlistname");
  const maxCharacterLimit = msStore.getPropertiesItem("maxListNameLength") || 40;
  const charactersRemainingText = msStore.getKeyValueItem(
    "maxcharacterlimittext"
  );
  const focusToTextField = () => {
    var _a;
    return (_a = textFieldInputRef == null ? void 0 : textFieldInputRef.current) == null ? void 0 : _a.focus();
  };
  useEffect(() => focusToTextField(), []);
  useEffect(() => {
    if (!wishLists.length) {
      const newDrawerContent = [
        {
          bodyContent: "createNewList",
          hasStickyBar: false,
          title: createNewListTitle
        }
      ];
      dispatch({
        payload: {
          drawerContent: [...newDrawerContent]
        },
        type: addToListActions.UPDATE_STATE
      });
    }
  }, [wishLists]);
  useEffect(() => {
    if (errorMsg || successMsg) {
      focusIntoRef(notificationRef);
    }
  }, [errorMsg, successMsg]);
  const getUpdatedDrawerContent = () => {
    let updatedDrawerContent = [];
    if (!drawerContent.find((drawer) => drawer.bodyContent === "saveToListUI")) {
      updatedDrawerContent.push({
        bodyContent: "saveToListUI",
        hasStickyBar: false,
        title: saveToListTitle
      });
    } else {
      updatedDrawerContent = drawerContent.filter(
        (drawer) => drawer.bodyContent !== "createNewList"
      );
    }
    return updatedDrawerContent;
  };
  const handleCreateNewListSuccess = (successMessage, newWishList, updatedWishLists) => {
    dispatch({
      payload: {
        drawerContent: getUpdatedDrawerContent(),
        isLoading: false,
        selectedLists: [newWishList],
        successMsg: successMessage,
        wishLists: [...updatedWishLists]
      },
      type: addToListActions.UPDATE_STATE
    });
    setListName("");
    setError(false);
    setListInputError("");
  };
  const handleCreateNewListLegacySuccess = (successMessage) => {
    dispatch({
      payload: {
        drawerContent: getUpdatedDrawerContent(),
        isLoading: true,
        successMsg: successMessage,
        // Legacy api does not have new list id in response, need to refetch lists
        wishListFetched: false
      },
      type: addToListActions.UPDATE_STATE
    });
    setListName("");
    setError(false);
    setListInputError("");
  };
  const handleCreateNewList = async () => {
    var _a;
    const listTitle = (_a = textFieldInputRef == null ? void 0 : textFieldInputRef.current) == null ? void 0 : _a.value;
    if (!listTitle) {
      setError(true);
      setListInputError(requiredListNameError);
      focusToTextField();
      return;
    }
    const trimmedTitle = listTitle.trim();
    if (!trimmedTitle) {
      setError(true);
      setListInputError(validListNameError);
      focusToTextField();
      return;
    }
    if (listName.length > maxCharacterLimit) return;
    setError(false);
    setListInputError("");
    try {
      const authCookie = await getToken();
      if (authCookie && trimmedTitle) {
        if (enableModernApis) {
          agent == null ? void 0 : agent.createNewWishList(
            authCookie,
            trimmedTitle,
            siteValue
          ).then((res) => {
            const status = res == null ? void 0 : res.status;
            const message = res == null ? void 0 : res.message;
            if (!status || !message) {
              dispatch({
                payload: {
                  errorMsg: createWishListError,
                  isLoading: false,
                  wishLists: wishLists || []
                },
                type: addToListActions.UPDATE_STATE
              });
              focusToTextField();
              return;
            }
            if (status === "error") {
              switch (message) {
                case "Prohibited":
                  setError(true);
                  setListInputError(validListNameError);
                  focusToTextField();
                  return;
                case "Duplicate":
                  setError(true);
                  setListInputError(
                    duplicateWishListError
                  );
                  focusToTextField();
                  return;
                default:
                  dispatch({
                    payload: {
                      errorMsg: createWishListError,
                      isLoading: false,
                      wishLists: wishLists || []
                    },
                    type: addToListActions.UPDATE_STATE
                  });
                  focusToTextField();
                  return;
              }
            } else {
              const response = res;
              const newWishList = {
                text: response.title,
                value: response.id
              };
              const successMsg2 = createWishListSuccess.replace(
                "{LIST}",
                trimmedTitle
              );
              const updatedWishLists = [
                newWishList,
                ...wishLists
              ];
              handleCreateNewListSuccess(
                successMsg2,
                newWishList,
                updatedWishLists
              );
            }
          }).catch((err) => {
            console.error(`Error creating list: ${err}`);
            const { response } = err;
            const { data } = response || {};
            const { message } = data || {};
            dispatch({
              payload: {
                errorMsg: message,
                isLoading: false,
                wishLists: wishLists || []
              },
              type: addToListActions.UPDATE_STATE
            });
            focusToTextField();
          });
        } else {
          agent == null ? void 0 : agent.addToListLegacy({
            clientIdLegacy,
            listName: trimmedTitle,
            locale,
            localeModuleKey: siteValue,
            operationName: "AddEmptyWishList"
          }).then((res) => {
            var _a2;
            const listName2 = (_a2 = res.listidname) == null ? void 0 : _a2[0];
            const errorMessageKey = res.errorMessageKey;
            if (!listName2 && !errorMessageKey) {
              dispatch({
                payload: {
                  errorMsg: createWishListError,
                  isLoading: false,
                  wishLists: wishLists || []
                },
                type: addToListActions.UPDATE_STATE
              });
              focusToTextField();
              return;
            }
            if (errorMessageKey) {
              switch (errorMessageKey) {
                case "_ERR_DUPLICATE_LIST":
                  setError(true);
                  setListInputError(
                    duplicateWishListError
                  );
                  focusToTextField();
                  return;
                default:
                  dispatch({
                    payload: {
                      errorMsg: createWishListError,
                      isLoading: false,
                      wishLists: wishLists || []
                    },
                    type: addToListActions.UPDATE_STATE
                  });
                  focusToTextField();
                  return;
              }
            } else {
              const successMsg2 = createWishListSuccess.replace(
                "{LIST}",
                trimmedTitle
              );
              handleCreateNewListLegacySuccess(successMsg2);
            }
          }).catch((err) => {
            console.error(`Error creating list: ${err}`);
            const { response } = err;
            const { data } = response || {};
            const { message } = data || {};
            dispatch({
              payload: {
                errorMsg: message,
                isLoading: false,
                wishLists: wishLists || []
              },
              type: addToListActions.UPDATE_STATE
            });
            focusToTextField();
          });
        }
      }
    } catch {
      console.error("Token fetch failed");
      focusToTextField();
    }
  };
  return /* @__PURE__ */ jsxs(Box, { children: [
    /* @__PURE__ */ jsx("span", { "aria-live": "polite", ref: notificationRef, tabIndex: -1, children: /* @__PURE__ */ jsx(ShowNotification, { errorMsg, successMsg }) }),
    /* @__PURE__ */ jsx(
      ProductUI,
      {
        dispatch,
        msStore,
        product,
        state
      }
    ),
    /* @__PURE__ */ jsxs(
      Box,
      {
        sx: {
          display: "flex",
          flexDirection: "column",
          gap: SpaceLg,
          marginTop: SpaceLg
        },
        children: [
          /* @__PURE__ */ jsx(
            TextField,
            {
              onChange: (e) => {
                const val = e.target.value;
                if (val.length > maxCharacterLimit) return;
                setError(false);
                setListInputError("");
                setListName(val);
              },
              onKeyDown: (e) => {
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
                if (e.key === "Enter") {
                  handleCreateNewList();
                }
              },
              sx: {
                minWidth: "100%"
              },
              type: "text",
              helpText: `${maxCharacterLimit - ((listName == null ? void 0 : listName.length) || 0)} ${charactersRemainingText}`,
              helpTextId: "help-text-create-new-list",
              errorText: listInputError ? listInputError : "",
              id: "create-new-list",
              inputRef: textFieldInputRef,
              isError,
              label: listNameFieldLabel,
              value: listName,
              charactersRemaining: maxCharacterLimit - ((listName == null ? void 0 : listName.length) || 0),
              isDynamicHelpText: true
            }
          ),
          /* @__PURE__ */ jsxs(
            Box,
            {
              sx: {
                display: "flex",
                flexDirection: "column",
                gap: SpaceSm
              },
              children: [
                /* @__PURE__ */ jsx(Button, { onClick: handleCreateNewList, variant: "primary", children: createNewListButtonTitle }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    onClick: () => {
                      setListName("");
                      setError(false);
                      setListInputError("");
                      if (!wishLists.length) {
                        dispatch({
                          payload: {
                            addToListModalOpen: false
                          },
                          type: addToListActions.UPDATE_STATE
                        });
                      } else {
                        dispatch({
                          payload: {
                            drawerContent: drawerContent.filter(
                              (drawer) => drawer.bodyContent !== "createNewList"
                            ),
                            errorMsg: "",
                            isLoading: false,
                            successMsg: "",
                            wishLists: wishLists || []
                          },
                          type: addToListActions.UPDATE_STATE
                        });
                      }
                      setListInputError("");
                    },
                    variant: "secondary",
                    children: cancelButtonLabel
                  }
                )
              ]
            }
          )
        ]
      }
    )
  ] });
};
export {
  CreateNewList
};
